import { Link } from "react-router-dom";
import { MessageCircle, Headphones } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { Cover, PlayPauseIcon } from "@/components/player/PlayerBits";
import { LikeButton } from "./LikeButton";
import { formatCount, formatTime } from "@/lib/format";

export const TrackCard = ({ track, list, actions }) => {
  const { track: current, playing, loading, playTrack } = usePlayer();
  const active = current?.id === track.id;

  return (
    <div data-testid={`track-card-${track.id}`} className={`group flex items-center gap-3 rounded-2xl border p-2.5 pr-3 transition-[border-color,background-color] duration-200 ${active ? "border-nw-red/50 bg-nw-red/[0.06]" : "border-white/10 bg-nw-surface/60 hover:border-white/20"}`}>
      <button data-testid={`track-play-${track.id}`} onClick={() => playTrack(track, list)} aria-label={`Play ${track.title}`} className="relative shrink-0">
        <Cover track={track} className="h-16 w-16 sm:h-[72px] sm:w-[72px] rounded-xl" />
        <span className={`absolute inset-0 grid place-items-center rounded-xl bg-black/50 text-white transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
          <PlayPauseIcon playing={active && playing} loading={active && loading} />
        </span>
      </button>
      <div className="min-w-0 flex-1">
        <Link to={`/track/${track.id}`} data-testid={`track-link-${track.id}`} className="block truncate font-display font-semibold text-white hover:text-nw-red transition-colors">{track.title}</Link>
        <p className="truncate text-xs text-slate-400">
          {track.artist_name}{track.owner && <> · <Link to={`/u/${track.owner.username}`} className="hover:text-white">@{track.owner.username}</Link></>}
        </p>
        <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-500">
          <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider">{track.genre}</span>
          <LikeButton track={track} />
          <Link to={`/track/${track.id}`} className="inline-flex items-center gap-1 hover:text-white"><MessageCircle className="h-4 w-4" />{formatCount(track.comment_count)}</Link>
          <span className="hidden sm:inline-flex items-center gap-1"><Headphones className="h-4 w-4" />{formatCount(track.qualified_plays)}</span>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className="font-mono text-[11px] text-slate-500">{formatTime(track.duration_seconds)}</span>
        {actions}
      </div>
    </div>
  );
};

export const TrackList = ({ tracks, empty, renderActions }) =>
  tracks.length === 0 ? (
    <p data-testid="track-list-empty" className="rounded-2xl border border-dashed border-white/15 p-6 text-sm text-slate-500">{empty}</p>
  ) : (
    <div data-testid="track-list" className="space-y-2.5">
      {tracks.map((t) => <TrackCard key={t.id} track={t} list={tracks} actions={renderActions?.(t)} />)}
    </div>
  );
